import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { AdminBar, EditButton, DeleteButton } from '@/components/AdminBar';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Loader2, Clock, ArrowLeft, Download, ArrowRight, ChevronRight, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIFF_BADGE = {
  base: { label: 'Base', color: 'bg-[#5f7d52]/15 text-[#4a5f43]' },
  intermedio: { label: 'Intermedio', color: 'bg-amber-100 text-amber-700' },
  avanzato: { label: 'Avanzato', color: 'bg-red-100 text-red-700' },
};

export default function ProtocolsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filterDiff, setFilterDiff] = useState('');
  const [filterDur, setFilterDur] = useState('');
  const canEdit = user?.is_admin || user?.is_editor || user?.role === 'admin' || user?.role === 'editor';

  const fetchProtocols = () => {
    axios.get(`${API}/protocols`).then(r => { setProtocols(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchProtocols(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Spostare nel cestino?')) return;
    try {
      const token = localStorage.getItem('biounica_token');
      await axios.delete(`${API}/editor/protocols/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Spostato nel cestino');
      fetchProtocols();
    } catch { toast.error('Errore'); }
  };

  const openProtocol = async (slug) => {
    try {
      const resp = await axios.get(`${API}/protocols/${slug}`);
      setSelected(resp.data);
    } catch { /* */ }
  };

  const filtered = protocols.filter(p => {
    if (filterDiff && p.difficulty !== filterDiff) return false;
    if (filterDur === 'short' && p.duration_weeks > 4) return false;
    if (filterDur === 'medium' && (p.duration_weeks < 4 || p.duration_weeks > 8)) return false;
    if (filterDur === 'long' && p.duration_weeks <= 8 && p.duration_weeks > 0) return false;
    if (filterDur === 'continuous' && p.duration_weeks !== 0) return false;
    return true;
  });

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#12100c]" /></div>;

  // Detail view
  if (selected) {
    const diff = DIFF_BADGE[selected.difficulty] || DIFF_BADGE.base;
    return (
      <div className="max-w-3xl" data-testid={`protocol-detail-${selected.slug}`}>
        <button onClick={() => setSelected(null)} className="text-sm text-[#4a4238] hover:text-[#12100c] mb-4 flex items-center gap-1" data-testid="protocol-back-btn">
          <ArrowLeft className="w-4 h-4" /> Torna ai protocolli
        </button>

        {/* Header */}
        <div className="flex items-start gap-4 mb-6">
          <span className="text-4xl">{selected.icon}</span>
          <div>
            <h1 className="font-heading text-xl sm:text-2xl font-bold text-[#12100c]">{selected.title}</h1>
            <div className="flex items-center gap-2 mt-1.5">
              <Badge className={`text-[10px] border-none font-semibold ${diff.color}`}>{diff.label}</Badge>
              <span className="text-xs text-[#4a4238] flex items-center gap-1">
                <Clock className="w-3 h-3" /> {selected.duration_weeks > 0 ? `${selected.duration_weeks} settimane` : 'Uso continuativo'}
              </span>
            </div>
          </div>
        </div>

        {/* Target */}
        <Card className="rounded-2xl border-[#5f7d52]/20 shadow-sm mb-4">
          <CardContent className="p-4 bg-[#5f7d52]/5">
            <p className="text-xs font-semibold text-[#4a5f43] uppercase tracking-wide mb-1">Per chi è indicato</p>
            <p className="text-sm text-[#12100c] leading-relaxed">{selected.target_description}</p>
          </CardContent>
        </Card>

        {/* Objective */}
        <p className="text-sm text-[#4a4238] mb-6 leading-relaxed"><span className="font-semibold text-[#12100c]">Obiettivo:</span> {selected.objective}</p>

        {/* Phases Timeline */}
        <h3 className="font-heading text-lg font-semibold text-[#12100c] mb-4">Fasi del protocollo</h3>
        <div className="relative pl-6 mb-6">
          {/* Vertical line */}
          <div className="absolute left-[9px] top-2 bottom-2 w-0.5 bg-[#5f7d52]/20" />
          <div className="space-y-4">
            {selected.phases.map((phase, i) => (
              <div key={i} className="relative" data-testid={`phase-${i}`}>
                <div className={`absolute -left-6 top-3 w-[18px] h-[18px] rounded-full border-2 border-[#5f7d52] ${i === 0 ? 'bg-[#5f7d52]' : 'bg-white'}`} />
                <Card className={`rounded-xl shadow-sm ${i % 2 === 0 ? 'bg-white border-gray-100' : 'bg-[#12100c]/[0.02] border-[#12100c]/10'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="text-[10px] border-[#5f7d52]/30 text-[#4a5f43]">{phase.week_range}</Badge>
                      <h4 className="font-semibold text-sm text-[#12100c]">{phase.phase_name}</h4>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-[#4a4238]">
                      <div><span className="font-semibold text-[#12100c]">Dosaggio:</span> {phase.dosage}</div>
                      <div><span className="font-semibold text-[#12100c]">Timing:</span> {phase.timing}</div>
                    </div>
                    <p className="text-xs text-[#4a4238] mt-2">{phase.instructions}</p>
                    {phase.notes && <p className="text-[10px] text-[#4a4238]/60 mt-1 italic">{phase.notes}</p>}
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>

        {/* Results */}
        <Card className="rounded-2xl border-gray-100 shadow-sm mb-4">
          <CardContent className="p-5">
            <h3 className="font-semibold text-sm text-[#12100c] mb-3">Risultati attesi</h3>
            <div className="space-y-2">
              {Object.entries(selected.expected_results).map(([k, v]) => (
                <div key={k} className="flex items-start gap-3">
                  <Badge className="bg-[#5f7d52]/10 text-[#4a5f43] border-none text-[10px] shrink-0 mt-0.5">{k.replace(/_/g, ' ')}</Badge>
                  <p className="text-sm text-[#4a4238]">{v}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Synergies */}
        <Card className="rounded-2xl border-[#c6a15b]/20 shadow-sm mb-4">
          <CardContent className="p-5 bg-[#c6a15b]/[0.05]">
            <h3 className="font-semibold text-sm text-[#12100c] mb-2">Sinergie consigliate</h3>
            <p className="text-sm text-[#4a4238] leading-relaxed">{selected.synergies}</p>
          </CardContent>
        </Card>

        {/* Contraindications */}
        {selected.contraindications && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-amber-800 mb-1">Precauzioni</p>
              <p className="text-sm text-amber-700">{selected.contraindications}</p>
            </div>
          </div>
        )}

        <Button disabled className="rounded-full border-gray-300 text-gray-400" variant="outline" data-testid="protocol-download-btn">
          <Download className="w-4 h-4 mr-2" /> Scarica protocollo PDF
        </Button>
      </div>
    );
  }

  // List view
  return (
    <div className="max-w-4xl" data-testid="protocols-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#12100c]">Protocolli UMIFUL</h1>
        <p className="text-sm text-[#4a4238] mt-2">Guide pratiche per i professionisti</p>
      </div>

      <AdminBar section="protocols" onAdd={() => navigate('/admin', { state: { tab: 'protocols', action: 'new' } })} />

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex gap-2 items-center">
          <span className="text-xs text-[#4a4238] font-medium">Durata:</span>
          {[{id:'',label:'Tutte'},{id:'short',label:'<4 sett.'},{id:'medium',label:'4-8 sett.'},{id:'long',label:'>8 sett.'},{id:'continuous',label:'Continuativo'}].map(f => (
            <button key={f.id} onClick={() => setFilterDur(f.id === filterDur ? '' : f.id)}
              className={`px-2.5 py-1 rounded-full text-[10px] font-medium border transition-all ${filterDur === f.id ? 'bg-[#12100c] text-white border-[#12100c]' : 'bg-white text-[#4a4238] border-gray-200'}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center">
          <span className="text-xs text-[#4a4238] font-medium">Livello:</span>
          {[{id:'',label:'Tutti'},{id:'base',label:'Base'},{id:'intermedio',label:'Intermedio'},{id:'avanzato',label:'Avanzato'}].map(f => (
            <button key={f.id} onClick={() => setFilterDiff(f.id === filterDiff ? '' : f.id)}
              className={`px-2.5 py-1 rounded-full text-[10px] font-medium border transition-all ${filterDiff === f.id ? 'bg-[#12100c] text-white border-[#12100c]' : 'bg-white text-[#4a4238] border-gray-200'}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {filtered.map(p => {
          const diff = DIFF_BADGE[p.difficulty] || DIFF_BADGE.base;
          return (
            <Card key={p.slug} className="rounded-2xl border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
              onClick={() => openProtocol(p.slug)} data-testid={`protocol-card-${p.slug}`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <span className="text-2xl shrink-0">{p.icon}</span>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm text-[#12100c] mb-1">{p.title}</h3>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={`text-[9px] border-none ${diff.color}`}>{diff.label}</Badge>
                      <span className="text-[10px] text-[#4a4238]">
                        {p.duration_weeks > 0 ? `${p.duration_weeks} sett.` : 'Continuativo'}
                      </span>
                    </div>
                    <p className="text-xs text-[#4a4238] line-clamp-2 leading-relaxed">{p.target_description}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {p.area_tags.map(t => (
                        <Badge key={t} variant="outline" className="text-[8px] border-gray-200 text-[#4a4238]">{t}</Badge>
                      ))}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#4a4238] shrink-0 mt-1" />
                </div>
                {canEdit && (
                  <div className="flex gap-1 mt-3 pt-2 border-t border-gray-100">
                    <EditButton onClick={(e) => { e.stopPropagation(); navigate('/admin', { state: { tab: 'protocols' } }); }} />
                    <DeleteButton onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }} />
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
